import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DeleteOutlined,
  DownloadOutlined,
  PlusOutlined,
  ReloadOutlined,
  UploadOutlined
} from "@ant-design/icons";
import { Alert, Button, Checkbox, Input, Popconfirm, Space, Table, Tag, message } from "antd";
import { useSelector } from "react-redux";
import {
  deleteReportDesign,
  exportReportDesign,
  fetchReportDesigns,
  importReportDesign
} from "./report-api";
import ReportDesignEditor from "./report-design-editor";
import { getReportTypeLabel, isLegacyReportType } from "./report-types";
import "./reports.css";

const ReportDesigns = () => {
  const law = useSelector((state) => state.user.session?.law);
  const canOpen = !law || law.length <= 81 || Number(law[81]) === 1;
  const inputRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [onlyNew, setOnlyNew] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    fetchReportDesigns()
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => setError(e.message || "Не удалось загрузить отчёты"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (canOpen) load();
  }, [canOpen, load]);

  const remove = async (record) => {
    try {
      await deleteReportDesign(record.id);
      message.success("Отчёт удалён");
      load();
    } catch (e) {
      message.error(e.message || "Не удалось удалить отчёт");
    }
  };

  const importFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    setLoading(true);
    let imported = 0;
    const errors = [];
    for (const file of files) {
      try {
        const parsed = JSON.parse(await file.text());
        const body = Array.isArray(parsed.body)
          ? parsed.body
          : typeof parsed.body === "string"
            ? JSON.parse(parsed.body)
            : [];
        await importReportDesign({
          ...parsed,
          id: 0,
          body: Array.isArray(body) ? body : []
        });
        imported += 1;
      } catch (e) {
        errors.push(file.name);
      }
    }
    setLoading(false);
    if (imported) {
      message.success(`Импортировано: ${imported}`);
      load();
    }
    if (errors.length) message.error(`Не импортированы: ${errors.join(", ")}`);
  };

  const selectedReport = rows.find((row) => row.id === selectedId) || null;

  const exportSelected = async () => {
    if (!selectedReport || isLegacyReportType(selectedReport.vid)) return;
    try {
      setLoading(true);
      const report = await exportReportDesign(selectedReport.id);
      const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${(report.name || "report").replace(/[\\/:*?"<>|]/g, "_")}.rep`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      message.error(e.message || "Не удалось экспортировать отчёт");
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (onlyNew && isLegacyReportType(row.vid)) return false;
      if (!query) return true;
      return [row.name, row.description, getReportTypeLabel(row.vid)].some((value) =>
        String(value || "").toLowerCase().includes(query)
      );
    });
  }, [rows, search, onlyNew]);

  const columns = [
    { title: "#", dataIndex: "id", width: 80 },
    { title: "Наименование", dataIndex: "name", ellipsis: true },
    { title: "Описание", dataIndex: "description", ellipsis: true },
    {
      title: "Вид",
      dataIndex: "vid",
      width: 190,
      render: (vid) =>
        isLegacyReportType(vid) ? (
          <Tag color="default">{getReportTypeLabel(vid)}</Tag>
        ) : (
          getReportTypeLabel(vid)
        )
    },
    {
      title: "",
      key: "actions",
      width: 52,
      align: "center",
      render: (_, record) =>
        isLegacyReportType(record.vid) ? null : (
          <Popconfirm
            title="Удалить отчёт?"
            okText="Удалить"
            cancelText="Отмена"
            onConfirm={() => remove(record)}
          >
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={(event) => event.stopPropagation()}
            />
          </Popconfirm>
        )
    }
  ];

  if (!canOpen) {
    return (
      <div className="report-designs">
        <Alert type="warning" showIcon message="Нет права просмотра отчётов" />
      </div>
    );
  }

  return (
    <div className="report-designs">
      <div className="report-designs__toolbar">
        <Space wrap>
          <Input
            allowClear
            value={search}
            placeholder="Поиск отчёта"
            className="report-designs__search"
            onChange={(event) => setSearch(event.target.value)}
          />
          <Checkbox checked={onlyNew} onChange={(event) => setOnlyNew(event.target.checked)}>
            Только новые
          </Checkbox>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditId(0)}>
            Добавить
          </Button>
          <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>
            Обновить
          </Button>
          <Button
            icon={<DownloadOutlined />}
            disabled={!selectedReport || isLegacyReportType(selectedReport.vid)}
            onClick={exportSelected}
          >
            Экспорт
          </Button>
          <Button icon={<UploadOutlined />} onClick={() => inputRef.current?.click()}>
            Импорт
          </Button>
          <input
            ref={inputRef}
            hidden
            type="file"
            accept=".rep"
            multiple
            onChange={importFiles}
          />
        </Space>
      </div>
      {error ? <Alert type="error" showIcon message={error} /> : null}
      <Table
        size="small"
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={filteredRows}
        pagination={false}
        scroll={{ x: 900 }}
        rowClassName={(record) =>
          [
            isLegacyReportType(record.vid) ? "report-designs__row--legacy" : "",
            record.id === selectedId ? "report-designs__row--selected" : ""
          ]
            .filter(Boolean)
            .join(" ")
        }
        onRow={(record) => ({
          onClick: () => {
            setSelectedId(record.id);
            setEditId(record.id);
          }
        })}
      />
      <ReportDesignEditor
        id={editId}
        open={editId != null}
        onClose={() => setEditId(null)}
        onSaved={() => {
          setEditId(null);
          load();
        }}
      />
    </div>
  );
};

export default ReportDesigns;
